import { storage, network } from '@carrent/platform'

/**
 * Клиент UI-телеметрии.
 *
 * Поток не является источником правды: серверный аудит говорит,
 * что произошло, телеметрия — что пользователь пытался сделать,
 * включая случаи, когда не произошло ничего.
 *
 * Отсюда все решения ниже: телеметрия никогда не должна мешать
 * основной работе приложения — ни ошибкой, ни задержкой, ни местом
 * в хранилище, ни трафиком.
 */

const FLUSH_INTERVAL_MS = 30_000
const MAX_BATCH = 50
/** Потолок офлайн-очереди. Дальше отбрасываем — см. приоритет ниже. */
const MAX_QUEUED = 200
const STORAGE_KEY = 'telemetry:queue'
const OPT_OUT_KEY = 'telemetry:disabled'

// Разрешённые ключи props. Сервер санитизирует повторно, но отсекать
// лишнее здесь дешевле: меньше трафика и меньше шансов, что
// пользовательский ввод вообще покинет устройство.
const ALLOWED_PROPS = new Set([
  'screen', 'step', 'step_index', 'steps_total', 'field',
  'filter_key', 'preset', 'action', 'count', 'duration_ms',
  'attempt', 'reason', 'category', 'phase',
])

let queue = []
let timer = null
let apiClient = null
let started = false

/** Отключено пользователем? Проверяется до любой постановки в очередь. */
export function isEnabled() {
  return storage.getRaw(OPT_OUT_KEY) !== '1'
}

export function setEnabled(enabled) {
  if (enabled) {
    storage.removeRaw(OPT_OUT_KEY)
  } else {
    storage.setRaw(OPT_OUT_KEY, '1')
    // Отключение должно быть немедленным: накопленное не отправляем.
    queue = []
    storage.remove(STORAGE_KEY)
  }
  return enabled
}

/**
 * Санитизация props.
 *
 * Ни одно поле формы и ни один введённый пользователем текст
 * не отправляются — только идентификаторы элементов и метрики.
 * Строки обрезаются: идентификатор в 64 символа не укладывается
 * разве что по ошибке, а пользовательский текст — укладывается.
 */
function sanitize(props) {
  if (!props || typeof props !== 'object') return undefined

  const clean = {}
  for (const [key, value] of Object.entries(props)) {
    if (!ALLOWED_PROPS.has(key)) continue
    if (typeof value === 'number' || typeof value === 'boolean') {
      clean[key] = value
    } else if (typeof value === 'string') {
      clean[key] = value.slice(0, 64)
    }
    // Объекты и массивы не пропускаем: в них легче всего
    // незаметно протащить введённый текст.
  }
  return Object.keys(clean).length ? clean : undefined
}

function persist() {
  // Низкий приоритет: если места нет, телеметрия отбрасывается первой
  // и молча. Она никогда не должна конкурировать с фотографиями
  // приёмки за квоту хранилища.
  const ok = storage.set(STORAGE_KEY, queue.slice(-MAX_QUEUED))
  if (!ok) queue = []
}

function restore() {
  const saved = storage.get(STORAGE_KEY)
  if (Array.isArray(saved)) queue = saved.slice(-MAX_QUEUED)
}

/**
 * Поставить событие в очередь.
 *
 * Никогда не бросает исключение и ничего не ждёт: вызывающий экран
 * не должен знать, что телеметрия существует.
 */
export function track(event, props) {
  if (!isEnabled() || !event) return

  try {
    queue.push({
      event,
      occurred_at: new Date().toISOString(),
      props: sanitize(props),
    })
    if (queue.length > MAX_QUEUED) {
      // Выбрасываем самое старое: свежие события полезнее.
      queue = queue.slice(-MAX_QUEUED)
    }
    persist()
  } catch {
    // Сбой телеметрии не может влиять на работу приложения.
  }
}

/** Отправить накопленное. Вызывается по таймеру и вручную. */
export async function flush() {
  if (!isEnabled() || queue.length === 0 || !apiClient) return
  if (!network.isOnline()) return

  const batch = queue.slice(0, MAX_BATCH)
  try {
    await apiClient.post('/telemetry', { events: batch })
    queue = queue.slice(batch.length)
    persist()
  } catch (err) {
    // 429 — сервер просит притормозить: очередь не трогаем,
    // следующая попытка будет через штатный интервал.
    // 4xx кроме 429 — событий сервер не примет и в другой раз,
    // держать их бессмысленно.
    const status = err?.response?.status
    if (status && status >= 400 && status < 500 && status !== 429) {
      queue = queue.slice(batch.length)
      persist()
    }
  }
}

/**
 * Отправка при уходе со страницы.
 *
 * Обычный запрос браузер отменяет при выгрузке, поэтому здесь
 * sendBeacon: он ставится в очередь и уходит уже после закрытия.
 * Именно эта отправка доносит wizard.abandoned — событие, ради
 * которого поток во многом и заводился.
 */
function flushOnUnload() {
  if (!isEnabled() || queue.length === 0) return

  const body = JSON.stringify({ events: queue.slice(0, MAX_BATCH) })
  const sent = navigator.sendBeacon?.(
    '/api/telemetry',
    new Blob([body], { type: 'application/json' })
  )

  // sendBeacon не несёт заголовок Authorization, поэтому сервер
  // отклонит запрос. Оставляем очередь на диске — отправим
  // при следующем запуске, когда токен снова будет доступен.
  if (!sent) persist()
}

/** Запустить сбор. Вызывается один раз из точки входа приложения. */
export function start(client) {
  if (started) return
  started = true
  apiClient = client

  restore()

  timer = setInterval(flush, FLUSH_INTERVAL_MS)

  // pagehide, а не beforeunload: на мобильных Safari beforeunload
  // не срабатывает при сворачивании приложения — а это самый
  // частый способ «уйти» с экрана на телефоне.
  globalThis.addEventListener?.('pagehide', flushOnUnload)
  globalThis.addEventListener?.('visibilitychange', () => {
    if (globalThis.document?.visibilityState === 'hidden') flush()
  })

  // При появлении сети разгружаем накопленное за офлайн.
  network.subscribe((online) => {
    if (online) flush()
  })
}

export function stop() {
  clearInterval(timer)
  timer = null
  started = false
}

/** Только для тестов. */
export function __reset() {
  queue = []
  stop()
  apiClient = null
  storage.remove(STORAGE_KEY)
  storage.removeRaw(OPT_OUT_KEY)
}

export function __queueSize() {
  return queue.length
}
